import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { checkInService } from '../../lib/checkInAttendanceService';
import { twilioService } from '../../lib/twilioService';
import Icon from '../../components/AppIcon';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import QRCodeGenerator from './components/QRCodeGenerator';
import QRCodeScanner from './components/QRCodeScanner';
import CheckInHistory from './components/CheckInHistory';

const MobileCheckInHub = () => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('qr-code');
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');
  const [showNavigation, setShowNavigation] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadCheckInHistory();
      generateQRCode();
    }
  }, [user]);

  const generateQRCode = async () => {
    const { data } = await checkInService?.generateQRCode(user?.id);
    if (data) {
      setQrCodeData(data);
    }
  };

  const loadCheckInHistory = async () => {
    setLoading(true);
    const { data, error } = await checkInService?.getMemberCheckIns(user?.id, 10);
    if (!error && data) {
      setCheckIns(data);
    }
    setLoading(false);
  };

  const handleCheckIn = async (method = 'qr_code') => {
    setLoading(true);
    const { data, error } = await checkInService?.createCheckIn({
      memberId: user?.id,
      gymId: userProfile?.gym_id,
      checkInMethod: method,
      qrCodeData: qrCodeData,
      location: 'Main Gym'
    });

    if (!error && data) {
      await loadCheckInHistory();
      
      // Send SMS confirmation
      await twilioService?.sendCheckInConfirmation(user?.id, {
        checkInId: data?.id,
        location: 'Main Gym'
      });
    }
    setLoading(false);
  };

  const tabs = [
    { id: 'qr-code', label: 'QR Code', icon: 'QrCode' },
    { id: 'scan', label: 'Scan', icon: 'ScanLine' },
    { id: 'history', label: 'History', icon: 'History' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hidden Navigation Bar with Mouseover */}
      <div
        className="fixed top-0 left-0 right-0 z-40"
        onMouseEnter={() => setShowNavigation(true)}
        onMouseLeave={() => setShowNavigation(false)}
      >
        {/* Hover trigger area */}
        <div className="h-4 bg-transparent"></div>
        
        {/* Navigation */}
        <div
          className={`transition-transform duration-300 ease-in-out ${
            showNavigation ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <RoleBasedHeader />
        </div>
      </div>

      {/* Main Content */}
      <div className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Mobile Check-In</h1>
            <p className="text-muted-foreground">Quick and easy gym access with QR codes</p>
          </div>

          {/* Tabs */}
          <div className="card mb-6">
            <div className="flex border-b border-border">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 font-medium transition-colors ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={20} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {activeTab === 'qr-code' && (
              <QRCodeGenerator
                qrCodeData={qrCodeData}
                userName={userProfile?.full_name}
                onCheckIn={handleCheckIn}
                loading={loading}
              />
            )}

            {activeTab === 'scan' && (
              <QRCodeScanner
                onScanSuccess={handleCheckIn}
                loading={loading}
              />
            )}

            {activeTab === 'history' && (
              <CheckInHistory
                checkIns={checkIns}
                loading={loading}
                onRefresh={loadCheckInHistory}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileCheckInHub;